import React, { useState, useEffect } from 'react';
import ParametricPanel, { PARAMETRIC_DEFAULTS, PARAMETRIC_CATEGORIES } from './ParametricPanel';

const styles = {
  panel: {
    border: '1px solid #e5e7eb', borderRadius: '10px',
    marginTop: '20px', overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  agentName: { fontSize: '13px', fontWeight: '700', color: '#111827' },
  activeBadge: {
    fontSize: '10px', fontWeight: '700', padding: '2px 8px',
    borderRadius: '20px', backgroundColor: '#dbeafe', color: '#0052CC',
  },
  proBadge: {
    fontSize: '10px', fontWeight: '700', padding: '2px 8px',
    borderRadius: '20px', backgroundColor: '#fef3c7', color: '#d97706',
  },
  body: { padding: '16px' },
  modeSwitcher: {
    display: 'flex', gap: '4px', marginBottom: '16px',
    backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '4px',
  },
  modeBtn: {
    flex: 1, padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
    textAlign: 'center', fontSize: '12px', fontWeight: '600',
    border: 'none', backgroundColor: 'transparent', color: '#6b7280',
    fontFamily: 'inherit',
  },
  modeBtnActive: {
    backgroundColor: '#fff', color: '#0052CC',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  categoryTab: {
    display: 'flex', gap: '4px', marginBottom: '16px',
    borderBottom: '2px solid #e5e7eb', paddingBottom: '0',
    overflowX: 'auto',
  },
  tab: {
    padding: '7px 14px', fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', border: 'none', background: 'none',
    color: '#6b7280', borderBottom: '2px solid transparent',
    marginBottom: '-2px', whiteSpace: 'nowrap', fontFamily: 'inherit',
    flexShrink: 0,
  },
  tabActive: { color: '#0052CC', borderBottom: '2px solid #0052CC' },
  tabBadge: {
    marginLeft: '5px', fontSize: '10px', fontWeight: '700',
    padding: '1px 5px', borderRadius: '10px',
    backgroundColor: '#e5e7eb', color: '#374151',
  },
  tabBadgeActive: { backgroundColor: '#0052CC', color: '#fff' },
  paramGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '14px',
  },
  paramLabel: {
    fontSize: '13px', fontWeight: '700', color: '#111827',
    marginBottom: '4px', display: 'block',
  },
  paramDesc: { fontSize: '11px', color: '#6b7280', marginBottom: '6px' },
  select: {
    width: '100%', padding: '7px 10px', borderRadius: '7px',
    border: '1px solid #d1d5db', fontSize: '12px',
    boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff',
  },
  selectActive: { border: '1px solid #0052CC', backgroundColor: '#EBF5FF' },
  disclaimer: {
    fontSize: '11px', color: '#6b7280', backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb', borderRadius: '6px',
    padding: '8px 10px', marginBottom: '14px', lineHeight: '1.5',
  },
  upgradeBox: {
    padding: '16px', borderRadius: '8px', border: '1px dashed #d1d5db',
    textAlign: 'center', backgroundColor: '#f9fafb',
  },
};

const ACOUSTIC_CATEGORIES = [
  {
    id: 'motor_speech', label: 'Motor Speech',
    params: [
      { key: 'vocal_tremor', label: 'Amplitude Modulation', desc: 'Sinusoidal amplitude + pitch modulation (~3–6 Hz)', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'pitch_instability', label: 'Pitch Modulation', desc: 'Dual-rate sinusoidal F0 perturbation', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'articulatory_imprecision', label: 'Spectral Muffling', desc: 'Low-pass filtering + spectral contrast reduction', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'strained_quality', label: 'Harmonic Compression', desc: 'Dynamic range compression + high-freq boost', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'irregular_rate', label: 'Piecewise Rate Variation', desc: 'Segment-level tempo perturbation (random walk)', options: ['none', 'mild', 'moderate', 'severe'] },
    ],
  },
  {
    id: 'voice', label: 'Voice',
    params: [
      { key: 'breathiness', label: 'Spectral Tilt + Aspiration', desc: 'High-shelf attenuation with envelope-following high-passed noise', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'hypophonia', label: 'Reduced Loudness', desc: 'Amplitude attenuation + dynamic range compression', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'hypernasality', label: 'Low-Freq Resonance Boost', desc: 'Multi-band EQ approximation of nasal coupling', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'pitch_variation', label: 'Pitch Range', desc: 'F0 range modification (monotone to exaggerated)', options: ['normal', 'monotone', 'exaggerated'] },
      { key: 'voice_quality', label: 'Voice Stability', desc: 'ElevenLabs stability/similarity perturbation', options: ['none', 'mild', 'moderate', 'severe'] },
    ],
  },
  {
    id: 'fluency', label: 'Fluency & Rate',
    params: [
      { key: 'speech_rate', label: 'Global Speech Rate', desc: 'TTS synthesis-level tempo reduction (0.7–1.0×)', options: ['none', 'mild', 'moderate', 'severe'] },
      { key: 'pause_length', label: 'Inter-Phrase Pause', desc: 'Ellipsis injection at clause boundaries', options: ['none', 'mild', 'moderate', 'severe'] },
    ],
  },
  {
    id: 'environment', label: 'Environment',
    params: [
      { key: 'background_noise', label: 'Background Noise', desc: 'Colored noise mixed at SNR-based level (anoisesrc + amix)', options: ['none', 'cafe', 'clinical', 'street', 'home'] },
      { key: 'channel_quality', label: 'Channel Bandwidth', desc: 'Bandpass filtering for transmission degradation', options: ['studio', 'telephone', 'degraded'] },
    ],
  },
];

const ACOUSTIC_DEFAULTS = {
  voice_quality: 'none', speech_rate: 'none', pause_length: 'none',
  background_noise: 'none', channel_quality: 'studio', pitch_variation: 'normal',
  vocal_tremor: 'none', breathiness: 'none', hypophonia: 'none',
  hypernasality: 'none', pitch_instability: 'none', articulatory_imprecision: 'none',
  irregular_rate: 'none', strained_quality: 'none',
};

function isActive(val, key) {
  if (key === 'channel_quality') return val !== 'studio';
  if (key === 'pitch_variation') return val !== 'normal';
  return val !== 'none';
}

function countCategoricalActive(config) {
  return Object.entries(config).filter(([k, v]) => isActive(v, k)).length;
}

export default function PerturbationPanel({ agentName, config, onChange, parametricConfig, onParametricChange, isPro }) {
  const [open, setOpen] = useState(false);
  
  const allFields = [];
  PARAMETRIC_CATEGORIES.forEach(cat => cat.params.forEach(p => p.fields.forEach(f => allFields.push(f))));

  function isParametricActive(pConfig) {
    if (!pConfig) return false;
    return allFields.some(f => pConfig[f.key] !== undefined && pConfig[f.key] !== f.default);
  }

  function isCategoricalActiveCheck(cConfig) {
    return countCategoricalActive(cConfig) > 0;
  }

  const mergedParametricForCheck = { ...PARAMETRIC_DEFAULTS, ...parametricConfig };
  const parametricHasActive = isParametricActive(mergedParametricForCheck);
  const categoricalHasActive = isCategoricalActiveCheck({ ...ACOUSTIC_DEFAULTS, ...config });

  const [mode, setMode] = useState('categorical');

  useEffect(() => {
    if (!isPro) return;
    if (parametricHasActive) setMode('parametric');
    else if (categoricalHasActive) setMode('categorical');
  }, [parametricHasActive, categoricalHasActive, isPro]);

  const [activeTab, setActiveTab] = useState('motor_speech');

  const merged = { ...ACOUSTIC_DEFAULTS, ...config };
  const mergedParametric = { ...PARAMETRIC_DEFAULTS, ...parametricConfig };

  const categoricalActiveCount = countCategoricalActive(merged);
  const parametricActiveCount = Object.entries(mergedParametric).filter(([k, v]) => {
    const allFields = [];
    PARAMETRIC_CATEGORIES.forEach(cat => cat.params.forEach(p => p.fields.forEach(f => allFields.push(f))));
    const field = allFields.find(f => f.key === k);
    return field && v !== field.default;
  }).length;

  const activeCount = parametricHasActive ? parametricActiveCount : categoricalActiveCount;
  const activeMode = parametricHasActive ? 'parametric' : (categoricalHasActive ? 'categorical' : null);

  function handleCategoricalChange(key, value) {
    onChange({ ...merged, [key]: value });
  }

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header} onClick={() => setOpen(o => !o)}>
        <div style={styles.headerLeft}>
          <span style={styles.agentName}>⚡ {agentName}</span>
          {activeCount > 0 && (
            <span style={styles.activeBadge}>{activeCount} active</span>
          )}
          {activeMode === 'parametric' && (
            <span style={styles.proBadge}>Parametric</span>
          )}
          {activeMode === 'categorical' && (
            <span style={{ ...styles.proBadge, backgroundColor: '#f3f4f6', color: '#374151' }}>Categorical</span>
          )}
        </div>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={styles.body}>

          {/* Mode switcher — Pro only */}
          {isPro && (
            <div style={styles.modeSwitcher}>
              {[
                { id: 'categorical', label: 'Categorical' },
                { id: 'parametric', label: 'Parametric ✦' },
              ].map(m => {
                const isDisabled = (m.id === 'categorical' && parametricHasActive) ||
                                   (m.id === 'parametric' && categoricalHasActive);
                return (
                  <button
                    key={m.id}
                    disabled={isDisabled}
                    style={{
                      ...styles.modeBtn,
                      ...(mode === m.id ? styles.modeBtnActive : {}),
                      ...(isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                    }}
                    onClick={() => {
                      if (isDisabled) return;
                      setMode(m.id);
                      if (m.id === 'parametric') {
                        onChange({ ...ACOUSTIC_DEFAULTS });
                      } else {
                        onParametricChange({ ...PARAMETRIC_DEFAULTS });
                      }
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}            </div>
          )}

          {/* Disclaimer */}
          <div style={styles.disclaimer}>
            ⚠️ These parameters produce controlled acoustic signal modifications. They are not clinically validated simulations of specific motor-speech disorders.
          </div>

          {/* CATEGORICAL MODE */}
          {mode === 'categorical' && (
            <>
              <div style={styles.categoryTab}>
                {ACOUSTIC_CATEGORIES.map(cat => {
                  const catActive = cat.params.filter(p => isActive(merged[p.key], p.key)).length;
                  const isTabActive = activeTab === cat.id;
                  return (
                    <button
                      key={cat.id}
                      style={{ ...styles.tab, ...(isTabActive ? styles.tabActive : {}) }}
                      onClick={() => setActiveTab(cat.id)}
                    >
                      {cat.label}
                      {catActive > 0 && (
                        <span style={{ ...styles.tabBadge, ...(isTabActive ? styles.tabBadgeActive : {}) }}>
                          {catActive}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {ACOUSTIC_CATEGORIES.filter(c => c.id === activeTab).map(cat => (
                <div key={cat.id} style={styles.paramGrid}>
                  {cat.params.map(param => {
                    const val = merged[param.key];
                    const active = isActive(val, param.key);
                    return (
                      <div key={param.key}>
                        <label style={styles.paramLabel}>{param.label}</label>
                        <div style={styles.paramDesc}>{param.desc}</div>
                        <select
                          style={{ ...styles.select, ...(active ? styles.selectActive : {}) }}
                          value={val}
                          onChange={e => handleCategoricalChange(param.key, e.target.value)}
                        >
                          {param.options.map(opt => (
                            <option key={opt} value={opt}>
                              {opt.charAt(0).toUpperCase() + opt.slice(1).replace(/_/g, ' ')}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Upgrade prompt for standard users */}
              {!isPro && (
                <div style={{ ...styles.upgradeBox, marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>
                    ✦ Parametric Controls
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    Set exact numeric values for each acoustic parameter. Available on the Professional plan.
                  </div>
                  <a href="mailto:info@converstation.ai" style={{ fontSize: '12px', color: '#0052CC', fontWeight: '600' }}>
                    Upgrade to Professional →
                  </a>
                </div>
              )}
            </>
          )}

          {/* PARAMETRIC MODE — Pro only */}
          {mode === 'parametric' && isPro && (
            <ParametricPanel
              values={mergedParametric}
              onChange={onParametricChange}
            />
          )}

        </div>
      )}
    </div>
  );
}
